import { IconBell, IconShare2, IconSquarePlus } from '@tabler/icons-solidjs'
import { Sheet, showToast } from '@veem/ui'
import { createEffect, createSignal, Match, Show, Switch } from 'solid-js'
import { api } from '../../lib/api.ts'
import { deviceSubscription, disablePush, enablePush, isIos, pushSupport } from '../../lib/push.ts'
import { pushKey } from '../state/inbox-news.ts'
import { Btn } from './button.tsx'
import { Pill } from './primitives.tsx'

const IphoneSteps = () => (
  <ol class="grid gap-2 text-body text-text">
    <li class="flex items-start gap-2.5">
      <IconShare2 class="mt-0.5 size-4 shrink-0 text-text-subtle" stroke-width={1.5} />
      <span>Відкрийте цей сайт у Safari і натисніть «Поділитися» внизу екрана.</span>
    </li>
    <li class="flex items-start gap-2.5">
      <IconSquarePlus class="mt-0.5 size-4 shrink-0 text-text-subtle" stroke-width={1.5} />
      <span>Оберіть «Додати на початковий екран», потім «Додати».</span>
    </li>
    <li class="flex items-start gap-2.5">
      <IconBell class="mt-0.5 size-4 shrink-0 text-text-subtle" stroke-width={1.5} />
      <span>Відкрийте «АМ CRM» з іконки на екрані і ввімкніть сповіщення тут.</span>
    </li>
  </ol>
)

export const PushSettings = (props: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const [busy, setBusy] = createSignal(false)
  const [error, setError] = createSignal('')
  const support = pushSupport()
  const [enabled, setEnabled] = createSignal(false)
  const refresh = async () => {
    setEnabled(
      support === 'ready' &&
        Notification.permission === 'granted' &&
        Boolean(await deviceSubscription()),
    )
  }
  createEffect(() => {
    if (props.open) void refresh()
  })
  const run = async (action: () => Promise<unknown>, done: string) => {
    if (busy()) return
    setBusy(true)
    setError('')
    try {
      await action()
      await refresh()
      showToast(done)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не вдалося змінити сповіщення')
    } finally {
      setBusy(false)
    }
  }
  const enable = () => {
    const key = pushKey()
    if (key) void run(() => enablePush(key), 'Сповіщення на цьому пристрої ввімкнено')
  }
  const disable = () => void run(disablePush, 'Сповіщення на цьому пристрої вимкнено')
  const test = () =>
    void run(() => api('/inbox/push-test', { method: 'POST', body: '{}' }), 'Перевірку надіслано')

  return (
    <Sheet
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Сповіщення на телефон"
      description="Коли вам доручать нове завдання, телефон або комп’ютер покаже сповіщення, навіть якщо CRM закрита."
      icon={<IconBell class="size-4" stroke-width={1.5} aria-hidden="true" />}
      footer={
        <Show when={pushKey() && support === 'ready'}>
          <Show
            when={enabled()}
            fallback={
              <Btn role="switch" aria-checked={false} onClick={enable} disabled={busy()}>
                Увімкнути на цьому пристрої
              </Btn>
            }
          >
            <Btn variant="secondary" onClick={test} disabled={busy()}>
              Надіслати перевірку
            </Btn>
            <Btn
              role="switch"
              aria-checked={true}
              variant="secondary"
              onClick={disable}
              disabled={busy()}
            >
              Вимкнути
            </Btn>
          </Show>
        </Show>
      }
    >
      <div class="grid gap-4" data-push-settings>
        <div class="flex items-center gap-2">
          <span class="text-body text-text-muted">Цей пристрій</span>
          <Switch>
            <Match when={!pushKey()}>
              <Pill>Не налаштовано</Pill>
            </Match>
            <Match when={enabled()}>
              <Pill tone="done">Увімкнено</Pill>
            </Match>
            <Match when={true}>
              <Pill>Вимкнено</Pill>
            </Match>
          </Switch>
        </div>
        <Switch>
          <Match when={!pushKey()}>
            <p class="text-body text-text-muted">
              Сповіщення на телефон ще не налаштовані на сервері. Нові завдання позначаються «нове»
              у «Мої завдання».
            </p>
          </Match>
          <Match when={support === 'unsupported'}>
            <p class="text-body text-text-muted">
              Цей браузер не підтримує сповіщення. Відкрийте CRM у Chrome, Edge або Safari.
            </p>
          </Match>
          <Match when={support === 'ready'}>
            <p class="text-body text-text-muted">
              Браузер спитає дозвіл. Натисніть «Дозволити». На кожному телефоні чи комп’ютері
              сповіщення вмикаються окремо.
            </p>
          </Match>
        </Switch>
        <Show when={error()}>
          <p role="alert" class="text-body text-status-critical">
            {error()}
          </p>
        </Show>
        <Show when={pushKey()}>
          <section
            data-iphone-hint
            class="grid gap-2.5 rounded-[0.75rem] p-3 shadow-[0_0_0_1px_var(--color-border)]"
            classList={{ 'bg-surface-muted': support !== 'needs-install' }}
          >
            <h3 class="text-body font-semibold">
              {support === 'needs-install' ? 'Спершу додайте CRM на екран iPhone' : 'На iPhone'}
            </h3>
            <Show when={support !== 'needs-install' && !isIos()}>
              <p class="text-caption text-text-muted">
                iPhone показує сповіщення лише для сайту, доданого на початковий екран.
              </p>
            </Show>
            <IphoneSteps />
          </section>
        </Show>
      </div>
    </Sheet>
  )
}
